import React, { useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { PriceTier } from 'apiClient/Api';
import { ArticleImageView, CalculatorApi } from './calculatorApi';
import { CalcImageData, ImageLightbox } from './CalcImage';

/** Editor für die graduierte Preisstaffel eines Artikels (mutiert via onChange). */
export const StaffelEditor: React.FC<{ staffel: PriceTier[]; onChange: (next: PriceTier[]) => void }> = ({
    staffel,
    onChange,
}) => (
    <Box sx={{ mt: 1, pl: 1, borderLeft: (t) => `2px solid ${t.palette.divider}` }}>
        <Typography variant="caption" color="text.secondary">
            Preisstaffel (graduiert: jede Bande gilt bis zu ihrer Grenze)
        </Typography>
        {staffel.map((tier, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center' }}>
                <TextField
                    label="bis (leer = offen)"
                    type="number"
                    size="small"
                    value={tier.upTo ?? ''}
                    onChange={(e) => {
                        const next = [...staffel];
                        next[i] = { ...tier, upTo: e.target.value === '' ? null : Number(e.target.value) };
                        onChange(next);
                    }}
                    slotProps={{ htmlInput: { step: 1, style: { width: 120 } } }}
                />
                <TextField
                    label="€/Stück"
                    type="number"
                    size="small"
                    value={tier.unitPrice ?? 0}
                    onChange={(e) => {
                        const next = [...staffel];
                        next[i] = { ...tier, unitPrice: Number(e.target.value) || 0 };
                        onChange(next);
                    }}
                    slotProps={{ htmlInput: { step: 0.01, style: { width: 120 } } }}
                />
                <IconButton size="small" color="error" onClick={() => onChange(staffel.filter((_, j) => j !== i))}>
                    <FontAwesomeIcon icon={faXmark} />
                </IconButton>
            </Stack>
        ))}
        <Button size="small" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={() => onChange([...staffel, { upTo: null, unitPrice: 0 }])}>
            Bande
        </Button>
    </Box>
);

/** Lädt die Bilder eines Artikels erst auf Klick und zeigt sie in der Lightbox. */
export const ArticleImagesButton: React.FC<{ api: CalculatorApi; articleNumber: string }> = ({ api, articleNumber }) => {
    const [open, setOpen] = useState(false);
    const [images, setImages] = useState<CalcImageData[] | null>(null);

    const handleOpen = async () => {
        if (!images) {
            try {
                const views: ArticleImageView[] = await api.getArticleImages(articleNumber);
                setImages(
                    views.map((v) => ({
                        articleNumber,
                        name: v.name,
                        titel: v.titel,
                        beschreibung: v.beschreibung,
                        haupt: v.haupt,
                        crop: v.crop,
                    })),
                );
            } catch {
                setImages([]);
            }
        }
        setOpen(true);
    };

    return (
        <>
            <Tooltip title="Bilder">
                <IconButton size="small" onClick={handleOpen}>
                    <FontAwesomeIcon icon={faImages} />
                </IconButton>
            </Tooltip>
            {images && images.length > 0 && (
                <ImageLightbox title={articleNumber} images={images} open={open} onClose={() => setOpen(false)} />
            )}
        </>
    );
};

/**
 * Generischer Editor für eine Liste gleichförmiger Datensätze (Parameter, artikel_ref, Optionen …).
 * Mutiert über onChange; `fields` rendert die Eingaben einer Zeile.
 */
export function ListEditor<T>({
    title,
    items,
    onChange,
    makeEmpty,
    renderRow,
}: {
    title: string;
    items: T[];
    onChange: (next: T[]) => void;
    makeEmpty: () => T;
    renderRow: (item: T, update: (patch: Partial<T>) => void) => React.ReactNode;
}) {
    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
                {title}
            </Typography>
            {items.map((item, i) => (
                <Stack key={i} direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center' }}>
                    {renderRow(item, (patch) => {
                        const next = [...items];
                        next[i] = { ...item, ...patch };
                        onChange(next);
                    })}
                    <IconButton size="small" color="error" onClick={() => onChange(items.filter((_, j) => j !== i))}>
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </Stack>
            ))}
            <Button size="small" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={() => onChange([...items, makeEmpty()])}>
                {title}
            </Button>
        </Box>
    );
}
